<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <header class="chc-header">
      <span class="chc-eyebrow">
        <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
        {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
      </span>
      <h1 class="chc-title">Nova <span>CHC</span></h1>
      <p class="chc-subtitle">
        Certidão de Habilitação de Crédito emitida pelo juízo do processo de origem. Use depois do prazo de habilitação e
        divergência do caso.
      </p>
    </header>

    <template v-if="user">
      <v-form ref="form" @submit.prevent="submit">
        <!-- Caso -->
        <article class="chc-card">
          <h2 class="chc-card__title">Caso</h2>
          <v-autocomplete v-model="form.client_id" :items="clients" item-value="id" item-text="name" label="Caso *"
            outlined dense hide-details="auto" :rules="[$rules.required]" :loading="loadingClients"
            no-data-text="Nenhum caso com prazo de habilitação encerrado" @change="onClientChange" />
          <div v-if="!loadingClients && !clients.length" class="chc-notice chc-notice--warn mt-3">
            <v-icon small color="#a16207">mdi-information-outline</v-icon>
            <div>
              Nenhum caso aceita CHC agora. Enquanto o prazo de habilitação e divergência estiver aberto, faça o pedido em
              <router-link to="/habilitacoes-divergencias/novo">Habilitações / Divergências</router-link>.
            </div>
          </div>
        </article>

        <!-- Processo de origem -->
        <article class="chc-card">
          <h2 class="chc-card__title">Processo de origem</h2>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.process_number" label="Número do processo *" outlined dense hide-details="auto"
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="6" md="2">
              <v-select v-model="form.process_state" :items="states" item-value="name" item-text="name" label="UF *"
                outlined dense hide-details="auto" :rules="[$rules.required]" />
            </v-col>
            <v-col cols="6" md="4">
              <v-select v-model="form.justice_branch" :items="justiceBranches" item-value="id" item-text="name"
                label="Ramo da justiça *" outlined dense hide-details="auto" :rules="[$rules.required]" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.court" label="Órgão julgador (vara) *" outlined dense hide-details="auto"
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.final_judgment_at" type="date" label="Trânsito em julgado" outlined dense
                hide-details="auto" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.calculation_updated_at" type="date" label="Cálculo atualizado em" outlined dense
                hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="form.notes" label="Observações" outlined dense rows="2" auto-grow hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-checkbox v-model="form.confidential" dense hide-details class="mt-0"
                label="Processo em segredo de justiça (confidencial)" />
            </v-col>
          </v-row>
        </article>

        <!-- Credores -->
        <article class="chc-card">
          <div class="chc-card__head">
            <h2 class="chc-card__title">Credores</h2>
            <v-btn text small color="darkSecondary" class="chc-link" @click="addCreditor">
              <v-icon left small>mdi-plus</v-icon> Adicionar credor
            </v-btn>
          </div>
          <v-row v-for="(creditor, index) in form.creditors" :key="creditor.key" dense align="center">
            <v-col cols="12" md="7">
              <v-text-field v-model="creditor.name" label="Nome *" outlined dense hide-details="auto"
                :rules="[$rules.required]" />
            </v-col>
            <v-col cols="10" md="4">
              <v-text-field v-model="creditor.registration" label="CPF/CNPJ *" outlined dense hide-details="auto"
                v-mask="['###.###.###-##', '##.###.###/####-##']" :rules="[$rules.required, validRegistration]" />
            </v-col>
            <v-col cols="2" md="1" class="text-right">
              <v-btn icon small :disabled="form.creditors.length === 1" title="Remover credor" @click="removeCreditor(index)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </article>

        <!-- Creditos -->
        <article class="chc-card">
          <div class="chc-card__head">
            <h2 class="chc-card__title">Créditos</h2>
            <v-btn text small color="darkSecondary" class="chc-link" @click="addCredit">
              <v-icon left small>mdi-plus</v-icon> Adicionar crédito
            </v-btn>
          </div>
          <div v-for="(credit, index) in form.credits" :key="credit.key" class="chc-item">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select v-model="credit.creditor_key" :items="creditorOptions" item-value="key" item-text="label"
                  label="Credor *" outlined dense hide-details="auto" :rules="[$rules.required]" />
              </v-col>
              <v-col v-if="companyOptions.length" cols="12" md="6">
                <v-select v-model="credit.client_company_id" :items="companyOptions" item-value="id" item-text="name"
                  label="Empresa devedora *" outlined dense hide-details="auto" :rules="[companyRequired]" />
              </v-col>
              <v-col cols="12" :md="companyOptions.length ? 12 : 6">
                <v-text-field v-model="credit.origin" label="Origem do crédito" placeholder="Ex.: Reclamatória trabalhista"
                  outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="credit.informed_class" :items="classes" item-value="id" item-text="name" label="Classe *"
                  outlined dense hide-details="auto" :rules="[$rules.required]" />
              </v-col>
              <v-col cols="5" md="3">
                <v-select v-model="credit.currency" :items="currencies" item-value="id" item-text="name" label="Moeda"
                  outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="7" md="4">
                <v-currency-field v-model="credit.amount" label="Valor *" :allowNegative="false" outlined dense
                  hide-details="auto" :rules="[$rules.required]" />
              </v-col>
              <v-col cols="12" md="1" class="text-right">
                <v-btn icon small :disabled="form.credits.length === 1" title="Remover crédito" @click="form.credits.splice(index, 1)">
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </article>

        <!-- Honorarios -->
        <article class="chc-card">
          <h2 class="chc-card__title">Honorários</h2>
          <v-checkbox v-model="form.has_attorney_fees" dense hide-details class="mt-0" label="Possui honorários advocatícios"
            @change="(value) => toggleFees('attorney', value)" />
          <template v-if="form.has_attorney_fees">
            <v-row v-for="(fee, index) in feesOf('attorney')" :key="`a-${index}`" dense class="chc-fee">
              <v-col cols="12" md="3"><v-text-field v-model="fee.name" label="Advogado(a) *" outlined dense hide-details="auto" :rules="[$rules.required]" /></v-col>
              <v-col cols="6" md="2"><v-text-field v-model="fee.registration" label="CPF" v-mask="'###.###.###-##'" outlined dense hide-details="auto" /></v-col>
              <v-col cols="3" md="2"><v-text-field v-model="fee.oab" label="OAB" outlined dense hide-details="auto" /></v-col>
              <v-col cols="3" md="2"><v-select v-model="fee.oab_state" :items="states" item-value="name" item-text="name" label="UF" outlined dense hide-details="auto" /></v-col>
              <v-col cols="10" md="2"><v-currency-field v-model="fee.amount" label="Valor *" :allowNegative="false" outlined dense hide-details="auto" :rules="[$rules.required]" /></v-col>
              <v-col cols="2" md="1" class="text-right">
                <v-btn icon small title="Remover" @click="removeFee(fee)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-btn text small color="darkSecondary" class="chc-link mb-2" @click="addFee('attorney')">
              <v-icon left small>mdi-plus</v-icon> Adicionar advogado(a)
            </v-btn>
          </template>

          <v-checkbox v-model="form.has_expert_fees" dense hide-details label="Possui honorários periciais"
            @change="(value) => toggleFees('expert', value)" />
          <template v-if="form.has_expert_fees">
            <v-row v-for="(fee, index) in feesOf('expert')" :key="`e-${index}`" dense class="chc-fee">
              <v-col cols="12" md="4"><v-text-field v-model="fee.name" label="Perito(a) *" outlined dense hide-details="auto" :rules="[$rules.required]" /></v-col>
              <v-col cols="6" md="3"><v-text-field v-model="fee.registration" label="CPF/CNPJ" v-mask="['###.###.###-##', '##.###.###/####-##']" outlined dense hide-details="auto" /></v-col>
              <v-col cols="6" md="2"><v-text-field v-model="fee.fixed_at" type="date" label="Fixado em" outlined dense hide-details="auto" /></v-col>
              <v-col cols="10" md="2"><v-currency-field v-model="fee.amount" label="Valor *" :allowNegative="false" outlined dense hide-details="auto" :rules="[$rules.required]" /></v-col>
              <v-col cols="2" md="1" class="text-right">
                <v-btn icon small title="Remover" @click="removeFee(fee)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-btn text small color="darkSecondary" class="chc-link" @click="addFee('expert')">
              <v-icon left small>mdi-plus</v-icon> Adicionar perito(a)
            </v-btn>
          </template>
        </article>

        <!-- Documentos -->
        <article class="chc-card">
          <h2 class="chc-card__title">Documentos</h2>
          <p class="chc-muted mb-3">PDF ou imagem, até 20 MB por arquivo.</p>
          <v-row dense>
            <v-col v-for="doc in documentKinds" :key="doc.kind" cols="12" md="6">
              <v-file-input v-model="files[doc.kind]" :label="doc.required ? `${doc.name} *` : doc.name" :hint="doc.hint"
                persistent-hint accept=".pdf,.png,.jpg,.jpeg" outlined dense prepend-icon="mdi-paperclip"
                :rules="doc.required ? [(value) => !!value || 'Anexe a CHC'] : []" />
            </v-col>
          </v-row>
        </article>

        <div class="chc-actions">
          <v-btn text rounded color="darkSecondary" class="chc-link" :disabled="saving" to="/certidoes-credito">Cancelar</v-btn>
          <v-btn depressed color="primary" class="chc-btn" type="submit" :loading="saving" :disabled="!clients.length">
            <v-icon left small>mdi-send-outline</v-icon> Enviar CHC
          </v-btn>
        </div>
      </v-form>
    </template>

    <login-required-notice v-else />

    <v-row><whatsButton /></v-row>
    <v-row><topButton /></v-row>
  </section>
</template>

<script>
import axios from 'axios';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import topButton from '@/components/layout/GoToTopButton.vue';
import whatsButton from '@/components/layout/WhatsAppButton.vue';
import statesList from '@/common/data/states-list.data';
import currencyTypes from '@/common/data/currency-types.data';
import creditorTypes from '@/common/data/creditor-types.data.js';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
let keySeed = 0;
const newKey = () => `k${++keySeed}`;

// CHC (Certidao de Habilitacao de Credito): depois do prazo de hab/div o credor
// ou o procurador envia a certidao do processo de origem; a AJ analisa e,
// aprovada, os creditos entram no QGC.
export default {
  name: 'CreateCreditCertificate',

  components: { topButton, whatsButton },

  data() {
    return {
      user: null,
      loadingClients: false,
      saving: false,
      clients: [],
      classes: creditorTypes,
      states: statesList,
      currencies: currencyTypes,
      justiceBranches: [
        { id: 'trabalho', name: 'Justiça do Trabalho' },
        { id: 'estadual', name: 'Justiça Estadual' },
        { id: 'federal', name: 'Justiça Federal' },
        { id: 'outra', name: 'Outra' },
      ],
      documentKinds: [
        { kind: 'chc', name: 'Certidão de Habilitação de Crédito (CHC)', required: true, hint: 'Certidão emitida pelo juízo de origem.' },
        { kind: 'calculation_decision', name: 'Decisão homologatória de cálculo', hint: '' },
        { kind: 'calculation_sheet', name: 'Planilha de cálculos', hint: '' },
        { kind: 'full_process', name: 'Processo completo', hint: 'Se o processo for confidencial, anexe a cópia integral.' },
      ],
      files: { chc: null, calculation_decision: null, calculation_sheet: null, full_process: null },
      form: this.emptyForm(),
    };
  },

  computed: {
    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },

    selectedClient() {
      return this.clients.find((client) => Number(client.id) === Number(this.form.client_id)) || null;
    },

    // Devedora principal (o proprio caso) + empresas do grupo; consolidacao substancial nao pede.
    companyOptions() {
      const client = this.selectedClient;
      if (!client || !Array.isArray(client.companies) || !client.companies.length) return [];
      if (Number(client.consolidation_type) === 2) return [];
      return [{ id: 0, name: client.name }, ...client.companies.map((company) => ({ id: company.id, name: company.name }))];
    },

    creditorOptions() {
      return this.form.creditors.map((creditor, index) => ({
        key: creditor.key,
        label: creditor.name ? `${creditor.name}${creditor.registration ? ` (${creditor.registration})` : ''}` : `Credor ${index + 1}`,
      }));
    },
  },

  created() {
    if (this.$root.user) {
      this.user = this.$root.user;
      this.loadUser();
      this.loadClients();
    }
  },

  methods: {
    emptyForm() {
      const creditorKey = newKey();
      return {
        client_id: null,
        process_number: '',
        process_state: null,
        justice_branch: 'trabalho',
        court: '',
        final_judgment_at: '',
        calculation_updated_at: '',
        notes: '',
        confidential: false,
        has_attorney_fees: false,
        has_expert_fees: false,
        creditors: [{ key: creditorKey, name: '', registration: '' }],
        credits: [this.newCredit(creditorKey)],
        fees: [],
      };
    },

    newCredit(creditorKey = null) {
      return { key: newKey(), creditor_key: creditorKey, client_company_id: null, origin: '', informed_class: 1, currency: 1, amount: null };
    },

    validRegistration(value) {
      const digits = String(value || '').replace(/\D/g, '');
      if (!digits) return true;
      return (digits.length === 11 ? cpf.isValid(digits) : digits.length === 14 && cnpj.isValid(digits)) || 'CPF/CNPJ inválido';
    },

    companyRequired(value) {
      return (value !== null && value !== undefined && value !== '') || 'Campo obrigatório';
    },

    // Credor enviando a propria CHC: ja vem como primeiro credor.
    async loadUser() {
      try {
        const result = await axios.get('/api/users/info');
        const info = result.data.content || {};
        if (Number(info.type) === 2) return;
        const first = this.form.creditors[0];
        if (!first.name && !first.registration) {
          first.name = info.name || '';
          first.registration = info.registration || '';
        }
      } catch (error) {
        // Sem preenchimento automatico.
      }
    },

    async loadClients() {
      this.loadingClients = true;
      try {
        const result = await axios.get('/api/clients', { params: { limit: 100000 } });
        const list = (result.data.content && result.data.content.data) || [];
        // So os casos com o prazo de hab/div encerrado e envio pelo portal liberado.
        this.clients = list.filter((client) => client.chc_open === true && (client.portal_habdiv_enabled === undefined
          ? Number(client.creditor_portal_enabled) !== 0
          : Number(client.portal_habdiv_enabled) !== 0));
        const fromQuery = Number(this.$route.query.client_id);
        if (fromQuery && this.clients.some((client) => Number(client.id) === fromQuery)) {
          this.form.client_id = fromQuery;
          this.onClientChange();
        }
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      } finally {
        this.loadingClients = false;
      }
    },

    async onClientChange() {
      this.form.credits.forEach((credit) => { credit.client_company_id = this.companyOptions.length ? null : 0; });
      this.classes = creditorTypes;
      if (!this.form.client_id) return;
      try {
        const result = await axios.get(`/api/clients/${this.form.client_id}/creditor-classes`);
        const data = (result.data.content && result.data.content.data) || [];
        if (data.length) this.classes = data.map((item) => ({ id: item.legacy_class_id, name: item.name }));
      } catch (error) {
        // Mantem as classes padrao.
      }
    },

    addCreditor() {
      this.form.creditors.push({ key: newKey(), name: '', registration: '' });
    },

    removeCreditor(index) {
      const [removed] = this.form.creditors.splice(index, 1);
      const fallback = this.form.creditors[0].key;
      this.form.credits.forEach((credit) => { if (credit.creditor_key === removed.key) credit.creditor_key = fallback; });
    },

    addCredit() {
      const credit = this.newCredit(this.form.creditors[0].key);
      if (!this.companyOptions.length) credit.client_company_id = 0;
      this.form.credits.push(credit);
    },

    feesOf(kind) {
      return this.form.fees.filter((fee) => fee.kind === kind);
    },

    addFee(kind) {
      this.form.fees.push({ kind, name: '', registration: '', oab: '', oab_state: null, amount: null, fixed_at: '' });
    },

    removeFee(fee) {
      this.form.fees.splice(this.form.fees.indexOf(fee), 1);
      if (!this.feesOf(fee.kind).length) {
        if (fee.kind === 'attorney') this.form.has_attorney_fees = false;
        else this.form.has_expert_fees = false;
      }
    },

    toggleFees(kind, value) {
      if (value && !this.feesOf(kind).length) this.addFee(kind);
      if (!value) this.form.fees = this.form.fees.filter((fee) => fee.kind !== kind);
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        this.$dialog.notify.error('Confira os campos obrigatórios.', { position: 'top-right', timeout: 3000 });
        return;
      }
      const tooLarge = Object.values(this.files).find((file) => file && file.size > MAX_FILE_SIZE);
      if (tooLarge) {
        this.$dialog.notify.error(`O arquivo "${tooLarge.name}" passa de 20 MB.`, { position: 'top-right', timeout: 5000 });
        return;
      }

      const payload = {
        ...this.form,
        credits: this.form.credits.map((credit) => ({ ...credit, client_company_id: credit.client_company_id || null })),
      };
      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));
      Object.keys(this.files).forEach((kind) => { if (this.files[kind]) formData.append(kind, this.files[kind]); });

      this.saving = true;
      try {
        const result = await axios.post('/api/credit-certificates', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        this.$dialog.notify.success(`CHC enviada. Protocolo ${result.data.content.protocol}.`, { position: 'top-right', timeout: 5000 });
        this.$router.push('/certidoes-credito');
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Mesmo padrao visual de "Documentos solicitados". */
.chc-header { padding: 32px 0 24px; }
.chc-eyebrow { display: flex; gap: 8px; align-items: center; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; color: #766386; }
.chc-title { margin: 14px 0 10px; font-size: clamp(28px, 3.4vw, 40px); font-weight: 700; color: #25113c; letter-spacing: -0.035em; line-height: 1.2; }
.chc-title span { color: #806697; }
.chc-subtitle { margin: 0; color: #73677c; font-size: 14px; max-width: 760px; }
.chc-muted { font-size: 13px; color: rgba(24, 0, 50, 0.55); }

.chc-card { padding: 20px 22px; border-radius: 16px; background: #fff; border: 1px solid rgba(24, 0, 50, 0.07); margin-bottom: 16px; }
.chc-card__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.chc-card__title { margin: 0 0 14px; font-size: 13px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #7e55ab; }
.chc-card__head .chc-card__title { margin-bottom: 10px; }
.chc-item { padding: 12px 0; border-top: 1px solid rgba(24, 0, 50, 0.06); }
.chc-item:first-of-type { border-top: 0; padding-top: 0; }
.chc-fee { margin-top: 4px; }

.chc-notice { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border-radius: 12px; background: rgba(120, 119, 229, 0.09); color: #2a1a40; font-size: 14px; line-height: 1.55; }
.chc-notice--warn { background: #fff7e6; }

.chc-link, .chc-btn { text-transform: none; letter-spacing: 0; font-weight: 600; }
.chc-btn { height: 40px !important; padding: 0 18px !important; border-radius: 10px; }
.chc-actions { display: flex; justify-content: flex-end; gap: 10px; margin: 8px 0 40px; }

.chc-card ::v-deep .v-text-field--outlined fieldset { border-radius: 10px; }
</style>
